import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { whatsappLink } from "@/lib/format";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export default function WhatsAppButton({
  phone, message = "", leadId, accountId, templateKey, size = "sm", label = "WhatsApp", testid = "whatsapp-btn",
}) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState(message);
  const [result, setResult] = useState("Sent");
  const [saving, setSaving] = useState(false);

  const openWhatsApp = () => {
    if (!phone) {
      toast.error("No phone number available");
      return;
    }
    window.open(whatsappLink(phone, message), "_blank", "noopener");
    setMsg(message);
    setOpen(true);
  };

  const logActivity = async () => {
    setSaving(true);
    try {
      await api.post("/activities/whatsapp-log", {
        lead_id: leadId || null, account_id: accountId || null,
        template_key: templateKey || null, message: msg, result,
      });
      toast.success("WhatsApp activity logged");
      setOpen(false);
    } catch (e) {
      toast.error("Could not log activity");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openWhatsApp}
        data-testid={testid}
        className={cn(
          "inline-flex items-center gap-2 rounded-lg bg-whatsapp hover:bg-whatsapp-dark text-white font-medium transition-colors",
          size === "sm" ? "px-3 min-h-[40px] text-sm" : "px-4 min-h-[44px]"
        )}
      >
        <MessageCircle className="h-4 w-4" /> {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="whatsapp-log-dialog">
          <DialogHeader>
            <DialogTitle>Log WhatsApp activity</DialogTitle>
            <DialogDescription>
              WhatsApp has opened in a new tab. This only opens WhatsApp — the message is <b>not</b> sent
              automatically. Review it there and press Send yourself, then log the outcome here (optional).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} data-testid="whatsapp-log-message" />
            <div className="flex gap-2 flex-wrap">
              {["Sent", "Follow-up Required", "No Response"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setResult(r)}
                  data-testid={`whatsapp-result-${r.toLowerCase().replace(/\s+/g, "-")}`}
                  className={cn(
                    "px-3 min-h-[40px] rounded-lg border text-sm font-medium",
                    result === r ? "border-teal bg-teal/10 text-teal" : "border-slate-200 text-slate-600"
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="whatsapp-log-skip">Skip</Button>
            <Button onClick={logActivity} disabled={saving} data-testid="whatsapp-log-save" className="bg-teal hover:bg-teal-700">
              {saving ? "Saving…" : "Log Activity"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
